import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { PackageSearch, Search, User } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { MobileHeader } from '@/components/layout/MobileHeader';
import { MobileButton } from '@/components/ui/MobileButton';
import { MobileSearchInput } from '@/components/ui/MobileSearchInput';
import { MobileBadge } from '@/components/ui/MobileBadge';
import { MobileCardGroup, MobileCardRow } from '@/components/ui/MobileCard';
import { MobileEmptyState } from '@/components/ui/MobileEmptyState';
import { MobileSkeleton } from '@/components/ui/MobileSkeleton';
import { PartyPicker } from '@/components/pickers/PartyPicker';

import { useAuthStore } from '@/app/store/authStore';
import { getPartyById } from '@/app/services/partyService';
import {
    attachPricing,
    listCategories,
    listItemsByIds,
    listTaxRates,
    listUnits,
    loadPricedItems,
} from '@/app/services/catalogService';
import {
    createSalesOrder,
    getPreviouslyOrderedItems,
} from '@/app/services/salesOrderService';
import { formatInr } from '@/app/lib/pricing';
import type { PricedItem } from '@/app/types/item';
import type { Party } from '@/app/types/party';
import type { SalesOrder, SalesOrderCreate } from '@/app/types/salesOrder';

import { useOrderCart } from './useOrderCart';
import { ProductRow } from './components/ProductRow';
import { CategoryChips } from './components/CategoryChips';
import { ReviewOrderSheet } from './components/ReviewOrderSheet';
import { TierSheet } from './components/TierSheet';
import { OrderSuccess } from './components/OrderSuccess';

const REFERENCE_QUERY_OPTIONS = { staleTime: Infinity, gcTime: Infinity } as const;

function todayIso(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
        d.getDate()
    ).padStart(2, '0')}`;
}

export default function NewSalesOrderPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const user = useAuthStore((state) => state.user);

    const userSiteIds = useMemo(() => user?.sites?.map((s) => s.id) ?? [], [user]);

    const [party, setParty] = useState<Party | null>(null);
    const [partyPickerOpen, setPartyPickerOpen] = useState(false);
    const [rawQuery, setRawQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [categoryId, setCategoryId] = useState<number | null>(null);
    const [reviewOpen, setReviewOpen] = useState(false);
    const [deliveryDate, setDeliveryDate] = useState('');
    const [notes, setNotes] = useState('');
    const [createdOrder, setCreatedOrder] = useState<SalesOrder | null>(null);
    // Item whose full bulk-price ladder is open, if any
    const [tierItem, setTierItem] = useState<PricedItem | null>(null);

    /**
     * The site the order belongs to.
     *
     * Taken from the party first: documents with a null site_id are invisible in the
     * ERP's site-filtered list (SQL `site_id = 4` never matches NULL), and most
     * manager accounts have no site of their own to fall back on.
     */
    const siteId = party?.site_id ?? user?.sites?.[0]?.id ?? null;

    // Priced for this party: the server picks its pricing mode.
    const pricingTarget = useMemo(
        () => (party ? { partyId: party.id, siteId } : null),
        [party, siteId]
    );

    const cart = useOrderCart();
    const { lines, grandTotal, itemCount, setQuantity, getQuantity, clear } = cart;

    // Prefill the customer when deep-linked from the party page.
    const prefillPartyId = searchParams.get('party_id');
    useEffect(() => {
        if (!prefillPartyId) return;
        const id = Number(prefillPartyId);
        if (!Number.isFinite(id) || id <= 0) return;
        let cancelled = false;
        getPartyById(id)
            .then((p) => {
                if (!cancelled) setParty(p);
            })
            .catch(() => {
                /* the picker remains available */
            });
        return () => {
            cancelled = true;
        };
    }, [prefillPartyId]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedQuery(rawQuery.trim()), 300);
        return () => clearTimeout(t);
    }, [rawQuery]);

    /* ---------------- reference data (small, cached for the session) ------------- */

    const { data: units } = useQuery({
        queryKey: ['units'],
        queryFn: listUnits,
        ...REFERENCE_QUERY_OPTIONS,
    });
    const { data: taxRates } = useQuery({
        queryKey: ['tax-rates'],
        queryFn: listTaxRates,
        ...REFERENCE_QUERY_OPTIONS,
    });
    const { data: categories } = useQuery({
        queryKey: ['categories'],
        queryFn: listCategories,
        ...REFERENCE_QUERY_OPTIONS,
    });

    const referenceReady = !!units && !!taxRates;
    const searching = debouncedQuery.length >= 2;

    /* ---------------- "usually ordered" for this party -------------------------- */

    const { data: usual, isLoading: usualLoading } = useQuery({
        queryKey: ['so-usual', party?.id, userSiteIds, siteId],
        enabled: !!pricingTarget && referenceReady,
        staleTime: 60_000,
        queryFn: async (): Promise<{
            items: PricedItem[];
            lastQuantities: Map<number, number>;
            priceModeLabel: string | null;
        }> => {
            const history = await getPreviouslyOrderedItems(party!.id, userSiteIds);
            const lastQuantities = new Map(history.map((h) => [h.item_id, h.lastQuantity]));
            if (history.length === 0) return { items: [], lastQuantities, priceModeLabel: null };

            const items = await listItemsByIds(history.map((h) => h.item_id));
            const { items: priced, priceModeLabel } = await attachPricing(
                items,
                units!,
                taxRates!,
                pricingTarget!
            );

            // Preserve the frequency/recency ranking from the invoice history.
            const rank = new Map(history.map((h, index) => [h.item_id, index]));
            priced.sort((a, b) => (rank.get(a.id) ?? 999) - (rank.get(b.id) ?? 999));

            return { items: priced, lastQuantities, priceModeLabel };
        },
    });

    const usualItems = usual?.items;
    const lastQuantityByItem = usual?.lastQuantities;
    const hasHistory = (usualItems?.length ?? 0) > 0;

    // Fall back to browsing when this party has never ordered.
    useEffect(() => {
        if (party && !usualLoading && !hasHistory && categoryId === null && categories?.length) {
            setCategoryId(categories[0].id);
        }
    }, [party, usualLoading, hasHistory, categoryId, categories]);

    /* ---------------- live product page (search or category) -------------------- */

    const { data: browseResult, isLoading: browseLoading, isFetching: browseFetching } =
        useQuery({
            queryKey: ['so-products', debouncedQuery, categoryId, pricingTarget],
            enabled: !!pricingTarget && referenceReady && (searching || categoryId !== null),
            staleTime: 30_000,
            queryFn: () =>
                loadPricedItems(
                    searching
                        ? { searchQuery: debouncedQuery }
                        : { categoryId: categoryId! },
                    units!,
                    taxRates!,
                    pricingTarget!
                ),
        });

    const visibleItems: PricedItem[] = searching || categoryId !== null
        ? browseResult?.items ?? []
        : usualItems ?? [];

    // The same for every page of this party; whichever query has answered says it.
    const priceModeLabel = browseResult?.priceModeLabel ?? usual?.priceModeLabel ?? null;

    const listLoading = searching || categoryId !== null
        ? browseLoading || (browseFetching && !browseResult)
        : usualLoading;

    const sectionTitle = searching
        ? `Results for "${debouncedQuery}"`
        : categoryId !== null
          ? categories?.find((c) => c.id === categoryId)?.name ?? 'Products'
          : 'Usually ordered';

    /* ---------------- submit ---------------------------------------------------- */

    const mutation = useMutation({
        mutationFn: createSalesOrder,
        onSuccess: (order) => {
            setReviewOpen(false);
            setCreatedOrder(order);
            clear();
        },
        onError: (err: unknown) => {
            let message = 'Failed to create order';
            if (axios.isAxiosError(err)) {
                message = err.response?.data?.detail || err.message || message;
            } else if (err instanceof Error) {
                message = err.message;
            }
            toast.error(message);
        },
    });

    const handleConfirm = () => {
        if (!party || siteId == null || lines.length === 0) return;

        const payload: SalesOrderCreate = {
            transaction_date: todayIso(),
            party_id: party.id,
            site_id: siteId,
            status: 'draft',
            items: lines.map((line) => ({
                item_id: line.item.id,
                quantity: String(line.quantity),
                unit_id: line.item.base_unit_id,
                rate: String(line.rate),
                ...(line.standardRate != null ? { standard_rate: String(line.standardRate) } : {}),
                ...(line.item.tax_rate_id ? { tax_rate_id: line.item.tax_rate_id } : {}),
            })),
            ...(deliveryDate ? { expected_delivery_date: deliveryDate } : {}),
            ...(notes.trim() ? { notes: notes.trim() } : {}),
            ...(user?.full_name ? { reference_number: `PWA · ${user.full_name}`.slice(0, 50) } : {}),
        };

        mutation.mutate(payload);
    };

    /* ---------------- success ---------------------------------------------------- */

    if (createdOrder) {
        return (
            <OrderSuccess
                documentNumber={createdOrder.document_number}
                partyName={createdOrder.party?.name ?? party?.name ?? ''}
                lineCount={createdOrder.items?.length ?? 0}
                totalAmount={Number(createdOrder.total_amount ?? 0)}
                onDone={() =>
                    navigate(party ? `/parties/${party.id}` : '/parties', { replace: true })
                }
                onViewOrders={() => navigate('/sales-orders', { replace: true })}
            />
        );
    }

    const siteMissing = !!party && siteId == null;
    const canReview = !!party && siteId != null && lines.length > 0;

    return (
        <div
            className="flex flex-col bg-background"
            style={{ minHeight: '100dvh', height: '100dvh' }}
        >
            <MobileHeader
                title="New Order"
                showBack
                rightAction={
                    itemCount > 0 ? (
                        <span className="text-xs font-semibold text-primary whitespace-nowrap">
                            {itemCount}
                        </span>
                    ) : undefined
                }
            />

            {/* Customer + filters stay fixed; only the product list scrolls. */}
            <div className="flex-shrink-0 border-b border-border">
                <div className="px-4 pt-3">
                    <MobileCardGroup>
                        <MobileCardRow onClick={() => setPartyPickerOpen(true)} showArrow>
                            {party ? (
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                        <span className="text-base font-semibold text-primary">
                                            {party.name.charAt(0).toUpperCase()}
                                        </span>
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-1.5 min-w-0">
                                            <p className="font-semibold text-foreground truncate">
                                                {party.name}
                                            </p>
                                            {priceModeLabel && (
                                                <MobileBadge variant="secondary" className="whitespace-nowrap flex-shrink-0">
                                                    {priceModeLabel}
                                                </MobileBadge>
                                            )}
                                        </div>
                                        {party.site?.name && (
                                            <p className="text-xs text-muted-foreground truncate">
                                                {party.site.name}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                                        <User className="h-5 w-5 text-muted-foreground" />
                                    </div>
                                    <span className="text-muted-foreground">Select customer</span>
                                </div>
                            )}
                        </MobileCardRow>
                    </MobileCardGroup>
                </div>

                {siteMissing && (
                    <p className="text-xs text-destructive px-5 pt-2">
                        This customer has no site assigned. Ask admin to set one before ordering.
                    </p>
                )}

                {party && !siteMissing && (
                    <>
                        <div className="px-4 pt-3">
                            <MobileSearchInput
                                value={rawQuery}
                                onValueChange={setRawQuery}
                                placeholder="Search products..."
                            />
                        </div>
                        {!searching && (
                            <CategoryChips
                                categories={categories ?? []}
                                selectedCategoryId={categoryId}
                                onSelect={setCategoryId}
                                showUsualChip={hasHistory}
                            />
                        )}
                    </>
                )}
            </div>

            <div className="flex-1 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                {!party ? (
                    <MobileEmptyState
                        icon={<User className="h-12 w-12" />}
                        title="Choose a customer"
                        description="Pick the store you're ordering for to get started"
                        action={
                            <MobileButton onClick={() => setPartyPickerOpen(true)}>
                                Select customer
                            </MobileButton>
                        }
                    />
                ) : siteMissing ? (
                    <MobileEmptyState
                        icon={<PackageSearch className="h-12 w-12" />}
                        title="Cannot order for this customer"
                        description="No site is assigned to them, so the order would not reach the office. Ask admin to set one."
                    />
                ) : (
                    <>
                        <h2 className="mobile-section-header pt-2">{sectionTitle}</h2>

                        {listLoading ? (
                            <ProductListSkeleton />
                        ) : visibleItems.length === 0 ? (
                            <MobileEmptyState
                                icon={<Search className="h-12 w-12" />}
                                title={searching ? 'No matches' : 'No products'}
                                description={
                                    searching
                                        ? 'Try a different product name or code'
                                        : 'Nothing in this category yet'
                                }
                            />
                        ) : (
                            <ul className="divide-y divide-border pb-40">
                                {visibleItems.map((item) => (
                                    <ProductRow
                                        key={item.id}
                                        item={item}
                                        quantity={getQuantity(item.id)}
                                        onQuantityChange={setQuantity}
                                        lastQuantity={lastQuantityByItem?.get(item.id)}
                                        onShowTiers={setTierItem}
                                    />
                                ))}
                            </ul>
                        )}
                    </>
                )}
            </div>

            {/* Sticky review bar */}
            <div
                className="border-t border-border bg-background"
                style={{
                    paddingTop: 12,
                    paddingLeft: 16,
                    paddingRight: 16,
                    paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 16px)',
                }}
            >
                <MobileButton
                    type="button"
                    size="lg"
                    className="w-full"
                    disabled={!canReview}
                    onClick={() => setReviewOpen(true)}
                >
                    {lines.length > 0
                        ? `${itemCount} ${itemCount === 1 ? 'item' : 'items'} · ${formatInr(grandTotal)} · Review`
                        : 'Add products to continue'}
                </MobileButton>
            </div>

            <PartyPicker
                open={partyPickerOpen}
                onClose={() => setPartyPickerOpen(false)}
                onSelect={(p) => {
                    setParty(p);
                    clear();
                    setCategoryId(null);
                    setRawQuery('');
                }}
                selectedPartyId={party?.id ?? null}
            />

            <ReviewOrderSheet
                open={reviewOpen}
                onClose={() => setReviewOpen(false)}
                onConfirm={handleConfirm}
                isSubmitting={mutation.isPending}
                party={party}
                lines={lines}
                grandTotal={grandTotal}
                onQuantityChange={setQuantity}
                deliveryDate={deliveryDate}
                onDeliveryDateChange={setDeliveryDate}
                notes={notes}
                onNotesChange={setNotes}
            />

            <TierSheet
                item={tierItem}
                quantity={tierItem ? getQuantity(tierItem.id) : 0}
                onClose={() => setTierItem(null)}
            />
        </div>
    );
}

function ProductListSkeleton() {
    return (
        <ul className="divide-y divide-border">
            {[1, 2, 3, 4, 5, 6].map((i) => (
                <li key={i} className="flex items-center gap-3 px-4 py-3 min-h-[72px]">
                    <div className="flex-1 space-y-2">
                        <MobileSkeleton className="h-4 w-48" />
                        <MobileSkeleton className="h-3 w-24" />
                    </div>
                    <MobileSkeleton className="w-11 h-11 rounded-xl" />
                </li>
            ))}
        </ul>
    );
}
